import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';

interface FooterLink {
  id: number;
  link_type: 'quick_link' | 'legal_link' | 'custom';
  name_en: string;
  name_ar: string;
  url: string;
  display_order: number;
  is_visible: boolean;
  is_builtin: boolean;
  icon?: string;
  opens_new_tab: boolean;
}

interface FooterLinkManagerProps {
  linkType: 'quick_link' | 'legal_link';
  title: string;
}

const FooterLinkManager: React.FC<FooterLinkManagerProps> = ({ linkType, title }) => {
  const [links, setLinks] = useState<FooterLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    url: '',
    icon: '',
    opens_new_tab: false
  });

  useEffect(() => {
    fetchLinks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkType]);

  const fetchLinks = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`https://patrixglobal.com/backend/api/admin/settings/footer/links?type=${linkType}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        setLinks(data.data);
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {      setMessage({ type: 'error', text: 'Failed to load links' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = async (link: FooterLink) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`https://patrixglobal.com/backend/api/admin/settings/footer/links/${link.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          is_visible: !link.is_visible
        }),
      });

      const data = await response.json();
      if (data.success) {
        setLinks(links.map(l => l.id === link.id ? { ...l, is_visible: !l.is_visible } : l));
        setMessage({ type: 'success', text: 'Link visibility updated' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update link' });
    }
  };

  const handleEdit = (link: FooterLink) => {
    setEditingId(link.id);
    setFormData({
      name_en: link.name_en,
      name_ar: link.name_ar,
      url: link.url,
      icon: link.icon || '',
      opens_new_tab: link.opens_new_tab
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setShowAddForm(false);
    setFormData({
      name_en: '',
      name_ar: '',
      url: '',
      icon: '',
      opens_new_tab: false
    });
  };

  const handleSave = async (linkId?: number) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');

      if (linkId) {
        // Update existing link
        const response = await fetch(`https://patrixglobal.com/backend/api/admin/settings/footer/links/${linkId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        const data = await response.json();
        if (data.success) {
          await fetchLinks();
          setEditingId(null);
          setMessage({ type: 'success', text: 'Link updated successfully' });
          setTimeout(() => setMessage(null), 3000);
        } else {
          setMessage({ type: 'error', text: data.message });
        }
      } else {
        // Create new link
        const response = await fetch('https://patrixglobal.com/backend/api/admin/settings/footer/links', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...formData,
            link_type: linkType
          }),
        });

        const data = await response.json();
        if (data.success) {
          await fetchLinks();
          setShowAddForm(false);
          setFormData({ name_en: '', name_ar: '', url: '', icon: '', opens_new_tab: false });
          setMessage({ type: 'success', text: 'Link created successfully' });
          setTimeout(() => setMessage(null), 3000);
        } else {
          setMessage({ type: 'error', text: data.message });
        }
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save link' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (link: FooterLink) => {
    if (link.is_builtin) {
      setMessage({ type: 'error', text: 'Cannot delete built-in links' });
      return;
    }

    if (!window.confirm('Are you sure you want to delete this link?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`https://patrixglobal.com/backend/api/admin/settings/footer/links/${link.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        await fetchLinks();
        setMessage({ type: 'success', text: 'Link deleted successfully' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete link' });
    }
  };

  const renderLinkForm = (linkId?: number) => (
    <div className="bg-gray-50 p-4 rounded-lg space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700">Name (English)</label>
          <input
            type="text"
            value={formData.name_en}
            onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            placeholder="Home"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Name (Arabic)</label>
          <input
            type="text"
            value={formData.name_ar}
            onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            placeholder="الرئيسية"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">URL</label>
        <input
          type="text"
          value={formData.url}
          onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          placeholder="/about"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700">Icon (optional)</label>
          <input
            type="text"
            value={formData.icon}
            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            placeholder="🏠 or icon-class"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.opens_new_tab}
              onChange={(e) => setFormData({ ...formData, opens_new_tab: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700">Open in new tab</span>
          </label>
        </div>
      </div>

      <div className="flex space-x-2">
        <button
          onClick={() => handleSave(linkId)}
          disabled={saving || !formData.name_en || !formData.name_ar || !formData.url}
          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : (linkId ? 'Update' : 'Add')}
        </button>
        <button
          onClick={handleCancelEdit}
          className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-medium text-gray-900">{title}</h4>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 text-sm"
          >
            + Add Link
          </button>
        )}
      </div>

      {message && (
        <div className={`rounded-md p-3 text-sm ${
          message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      {showAddForm && renderLinkForm()}

      <div className="space-y-2">
        {links.map((link) => (
          <div key={link.id} className="border rounded-lg p-4 bg-white">
            {editingId === link.id ? (
              renderLinkForm(link.id)
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    {link.icon && <span className="text-xl">{link.icon}</span>}
                    <div>
                      <p className="font-medium text-gray-900">
                        {link.name_en} / {link.name_ar}
                        {link.is_builtin && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            Built-in
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-gray-500">{link.url}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleToggleVisibility(link)}
                    className={`px-3 py-1 rounded text-sm ${
                      link.is_visible
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {link.is_visible ? 'Visible' : 'Hidden'}
                  </button>

                  <button
                    onClick={() => handleEdit(link)}
                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                  >
                    Edit
                  </button>

                  {!link.is_builtin && (
                    <button
                      onClick={() => handleDelete(link)}
                      className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FooterLinkManager;